/**
 * Helpers for reading the `claro_prefilled` cookie set by the backend's
 * magic-link consume flow.
 *
 * When an inviter attaches `prefilled_data` to a magic-link invite (e.g.
 * AHPRA number, name, practitioner type — fields they already know about
 * the invitee from their own records), the backend persists it on the
 * token, returns it on consume, and drops it into a short-lived
 * non-httpOnly cookie before redirecting to /. The wizard reads the
 * cookie on mount, renders confirmation cards instead of capture screens,
 * and clears the cookie after submission.
 *
 * Cookie format: URL-encoded JSON. Non-sensitive on its own (we'd be
 * showing the data to the user anyway); the actual practitioner identity
 * is set on the user record only after she explicitly submits the wizard.
 */

const COOKIE_NAME = 'claro_prefilled'

export interface PrefilledClinic {
  clinic_name: string
  org_name?: string
  address?: string
}

export interface PrefilledData {
  first_name?: string
  last_name?: string
  practitioner_type?: 'registered' | 'non_registered' | 'admin_only'
  ahpra_number?: string
  qualifications?: string
  /**
   * Clinics the inviter pre-created at magic-link consume (CLA-658). When
   * present, the backend has already provisioned real `orgs` + `clinics`
   * rows for each entry — the wizard renders a confirmation card listing
   * them rather than the capture form.
   */
  clinics?: PrefilledClinic[]
  /** Free-form pass-through — additional fields the inviter chose to include. */
  [key: string]: unknown
}

/** Read the `claro_prefilled` cookie and parse its JSON payload. */
export function readPrefilledData(): PrefilledData | null {
  const raw = readCookie(COOKIE_NAME)
  if (!raw) return null
  try {
    const decoded = decodeURIComponent(raw)
    const parsed = JSON.parse(decoded) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as PrefilledData
    }
    return null
  } catch {
    return null
  }
}

/**
 * Clear the `claro_prefilled` cookie. Call after the wizard has either
 * accepted the prefill (submitted) or rejected it (user picked "edit"
 * and submitted manual values), so subsequent visits don't see stale
 * data.
 *
 * Pass `{ keepClinics: true }` from steps that consume only the identity
 * fields (PractitionerIdentity) so the later clinic-confirmation step
 * (FirstClinic / CLA-658) still sees the prefilled clinics in the cookie.
 * Without that flag, the cookie's `clinics` field would be wiped before
 * the user reached the step that needs it.
 */
export function clearPrefilledData(opts: { keepClinics?: boolean } = {}): void {
  if (opts.keepClinics) {
    const current = readPrefilledData()
    const clinics = current?.clinics
    if (Array.isArray(clinics) && clinics.length > 0) {
      const trimmed: PrefilledData = { clinics }
      const encoded = encodeURIComponent(JSON.stringify(trimmed))
      // Match the backend's max-age (30 min); shorter is fine since the
      // wizard only needs the data for a few seconds more, but matching
      // means a refresh-during-wizard still works.
      document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=1800`
      return
    }
  }
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`))
  if (!match) return null
  const value = match.slice(name.length + 1)
  return value || null
}
